import React, { useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';
import {
  Activity,
  Building2,
  CalendarPlus,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Clock3,
  Download,
  History,
  Info,
  Loader2,
  Pencil,
  Search,
  Stethoscope,
  Undo2,
  Users,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useIsDoctor } from '@/hooks/useIsDoctor';
import { useDoctorOnlyMode } from '@/hooks/useDoctorOnlyMode';
import { useToast } from '@/hooks/use-toast';
import {
  getMyPatientCases,
  listCaseActivities,
  PatientCaseActivityWithDetails,
  PatientCaseStatus,
  PatientCaseWithDetails,
  updatePatientCase,
} from '@/services/patientCaseService';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { cn } from '@/lib/utils';
import SchedulerHeader from '@/components/SchedulerHeader';
import PatientVisitHistory from '@/components/PatientVisitHistory';
import DoctorReferralTrend from '@/components/DoctorReferralTrend';
import { getCurrentSingaporeDate } from '@/lib/dateUtils';
import { datedFilename, exportRowsToCsv } from '@/lib/exportCsv';
import {
  groupByReferrer,
  isActiveInMonth,
  isNewInMonth,
  ReferrerGroup,
  referrerKey,
  singaporeMonthKey,
} from '@/lib/myPatientsGroups';

// ---------------------------------------------------------------------------
// Status band metadata
//
// Mirrors the visual conventions in PatientsView.tsx without importing from
// that file (Agent 4 is editing it in parallel for backlog #21). The labels
// and colour families are intentionally aligned so doctors who switch
// between views see consistent badges.
// ---------------------------------------------------------------------------

type StatusBand = 'action' | 'in_progress' | 'closed';

const STATUS_META: Record<
  string,
  { label: string; badgeClassName: string; band: StatusBand }
> = {
  new_referral: {
    label: 'New Referral',
    badgeClassName: 'border-amber-300 bg-amber-50 text-amber-900',
    band: 'action',
  },
  appointment_scheduled: {
    label: 'Appointment Scheduled',
    badgeClassName: 'border-blue-300 bg-blue-50 text-blue-900',
    band: 'in_progress',
  },
  patient_seen: {
    label: 'Patient Seen',
    badgeClassName: 'border-cyan-300 bg-cyan-50 text-cyan-900',
    band: 'in_progress',
  },
  treatment_proposed: {
    label: 'Treatment Proposed',
    badgeClassName: 'border-indigo-300 bg-indigo-50 text-indigo-900',
    band: 'action',
  },
  treatment_accepted: {
    label: 'Treatment Accepted',
    badgeClassName: 'border-emerald-300 bg-emerald-50 text-emerald-900',
    band: 'in_progress',
  },
  treatment_ongoing: {
    label: 'Ongoing Treatment',
    badgeClassName: 'border-sky-300 bg-sky-50 text-sky-900',
    band: 'in_progress',
  },
  referred_to_other_specialty: {
    label: 'Referred to Other Specialty',
    badgeClassName: 'border-purple-300 bg-purple-50 text-purple-900',
    band: 'closed',
  },
  no_show: {
    label: 'No Show',
    badgeClassName: 'border-orange-300 bg-orange-50 text-orange-900',
    band: 'action',
  },
  non_compliant: {
    label: 'Non-compliant / Not Returning',
    badgeClassName: 'border-red-300 bg-red-50 text-red-900',
    band: 'closed',
  },
  referred_back: {
    label: 'Referred Back',
    badgeClassName: 'border-violet-300 bg-violet-50 text-violet-900',
    band: 'closed',
  },
  patient_did_not_proceed: {
    label: 'Patient Did Not Proceed',
    badgeClassName: 'border-rose-300 bg-rose-50 text-rose-900',
    band: 'closed',
  },
  lost_inactive: {
    label: 'Lost / Inactive',
    badgeClassName: 'border-slate-300 bg-slate-100 text-slate-700',
    band: 'closed',
  },
};

const FALLBACK_STATUS_META = {
  label: 'Unknown',
  badgeClassName: 'border-slate-300 bg-slate-100 text-slate-700',
  band: 'in_progress' as StatusBand,
};

// Completed and Referred Back are one status, `referred_back` (Samuel, 29 Sep
// 2026). A row the backfill migration has not reached yet still says
// `completed`; read it as Referred Back everywhere on this page.
const canonicalStatus = (status: string | null | undefined): string =>
  status === 'completed' ? 'referred_back' : status ?? '';

const getStatusMeta = (status: string | null | undefined) => {
  if (!status) return FALLBACK_STATUS_META;
  return STATUS_META[canonicalStatus(status)] ?? FALLBACK_STATUS_META;
};

// Statuses a doctor can set on their own cases (Samuel 2026-09-17). Mirrors the
// admin case-detail ladder in PatientsView (main funnel first, off-ramps
// second) so doctors and staff describe the same journey with the same words.
const DOCTOR_MAIN_LADDER: PatientCaseStatus[] = [
  'new_referral',
  'appointment_scheduled',
  'patient_seen',
  'treatment_proposed',
  'treatment_accepted',
  'treatment_ongoing',
  'referred_back',
];

const DOCTOR_OFF_RAMPS: PatientCaseStatus[] = [
  'referred_to_other_specialty',
  'no_show',
  'non_compliant',
  'patient_did_not_proceed',
  'lost_inactive',
];

const ACTIVITY_LABELS: Record<string, string> = {
  status_change: 'Status',
  communication: 'Communication',
  ownership_change: 'Owner',
  note: 'Note',
  referred_back: 'Referred back',
};

const activitySummary = (activity: PatientCaseActivityWithDetails): string => {
  if (activity.activity_type === 'status_change') {
    const from = activity.from_value ? getStatusMeta(activity.from_value).label : '—';
    const to = activity.to_value ? getStatusMeta(activity.to_value).label : '—';
    return `${from} → ${to}`;
  }
  return activity.note ?? ACTIVITY_LABELS[activity.activity_type] ?? activity.activity_type;
};

const formatDateSafe = (value: string | null | undefined, pattern = 'd MMM yyyy') => {
  if (!value) return null;
  try {
    return format(parseISO(value), pattern);
  } catch {
    return null;
  }
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

type PeriodFilter = 'all' | 'this_month' | 'last_month' | 'last_3_months' | 'this_year' | 'custom';

const PERIOD_OPTIONS: [PeriodFilter, string][] = [
  ['all', 'All period'],
  ['this_month', 'This month'],
  ['last_month', 'Last month'],
  ['last_3_months', 'Last 3 months'],
  ['this_year', 'This year'],
  ['custom', 'Custom range'],
];

const singaporeToday = () =>
  new Intl.DateTimeFormat('en-CA', { timeZone: 'Asia/Singapore' }).format(new Date());

const ymd = (year: number, month: number, day: number) =>
  new Date(Date.UTC(year, month - 1, day)).toISOString().slice(0, 10);

// Inclusive YYYY-MM-DD bounds; empty string means open-ended.
const periodRange = (period: PeriodFilter, customFrom: string, customTo: string) => {
  const today = singaporeToday();
  const [year, month] = today.split('-').map(Number);
  switch (period) {
    case 'this_month':
      return { from: ymd(year, month, 1), to: today };
    case 'last_month':
      return { from: ymd(year, month - 1, 1), to: ymd(year, month, 0) };
    case 'last_3_months':
      return { from: ymd(year, month - 2, 1), to: today };
    case 'this_year':
      return { from: ymd(year, 1, 1), to: today };
    case 'custom':
      return { from: customFrom, to: customTo };
    default:
      return { from: '', to: '' };
  }
};

const MyPatients: React.FC = () => {
  const navigate = useNavigate();
  const { caseId } = useParams<{ caseId?: string }>();
  const { user } = useAuth();
  const { isDoctor, isLoading: isCheckingDoctor, staffId } = useIsDoctor();
  const { doctorOnly } = useDoctorOnlyMode();
  const { toast } = useToast();

  // SchedulerHeader is shared with the rest of the dashboard. The date
  // navigator is not meaningful on this page, so we hold a local stub date
  // and ignore navigation callbacks — clicking another view navigates away.
  const [headerDate, setHeaderDate] = useState(getCurrentSingaporeDate());
  const [closedExpanded, setClosedExpanded] = useState(false);
  const [statusCase, setStatusCase] = useState<PatientCaseWithDetails | null>(null);
  const [statusValue, setStatusValue] = useState('');
  const [isSavingStatus, setIsSavingStatus] = useState(false);
  const [detailCase, setDetailCase] = useState<PatientCaseWithDetails | null>(null);
  const [pendingCaseId, setPendingCaseId] = useState<string | null>(null);

  // Deep link /my-patients/:caseId — open the case inline. Doctor-only
  // logins cannot use /patients/:id (Index bounces them back here), so the
  // detail lives on this page (Samuel 2026-09-17: clicking a patient must
  // go directly into the patient).
  React.useEffect(() => {
    if (caseId) {
      setPendingCaseId(caseId);
      navigate('/my-patients', { replace: true });
    }
  }, [caseId, navigate]);

  const {
    data: cases = [],
    isLoading: isLoadingCases,
    isError,
    refetch,
    isRefetching,
  } = useQuery({
    queryKey: ['my-patient-cases', staffId],
    queryFn: () => getMyPatientCases(staffId ?? ''),
    enabled: Boolean(staffId) && isDoctor,
  });

  // Case history for the open patient detail. Same activity feed staff see.
  const {
    data: detailActivities = [],
    isLoading: isLoadingActivities,
  } = useQuery({
    queryKey: ['my-patient-case-activities', detailCase?.id],
    queryFn: () => listCaseActivities(detailCase!.id),
    enabled: Boolean(detailCase),
  });

  React.useEffect(() => {
    if (pendingCaseId && cases.length > 0) {
      const found = cases.find((record) => record.id === pendingCaseId);
      if (found) setDetailCase(found);
      setPendingCaseId(null);
    }
  }, [pendingCaseId, cases]);

  const grouped = useMemo(() => {
    const result: Record<StatusBand, PatientCaseWithDetails[]> = {
      action: [],
      in_progress: [],
      closed: [],
    };

    for (const caseRecord of cases) {
      const meta = getStatusMeta(caseRecord.status);
      result[meta.band].push(caseRecord);
    }

    return result;
  }, [cases]);

  const summary = useMemo(() => {
    const month = singaporeMonthKey(new Date()) ?? '';
    const isClosed = (c: PatientCaseWithDetails) => getStatusMeta(c.status).band === 'closed';
    const active = grouped.action.length + grouped.in_progress.length;
    // Count by status, like the Closed tab and "Referred back this month". The
    // flag lags the status on cases closed by the Plato import before 29 Sep and
    // on cases a doctor moves to Referred Back with the Status button (5 Oct:
    // Dr Surein's card read 69 against 76 Referred Back cases).
    const referredBack = cases.filter(
      (c) => canonicalStatus(c.status) === 'referred_back',
    ).length;
    const newThisMonth = cases.filter((c) => isNewInMonth(c, month)).length;
    const allThisMonth = cases.filter((c) => isActiveInMonth(c, isClosed(c), month)).length;
    const monthLabel = new Intl.DateTimeFormat('en-SG', {
      month: 'long',
      timeZone: 'Asia/Singapore',
    }).format(new Date());
    return { active, referredBack, newThisMonth, allThisMonth, monthLabel };
  }, [cases, grouped]);

  // Referral tabs (Samuel, 8 Oct 2026). Built only from this doctor's own
  // cases, so a group never shows another doctor's patients.
  const dentistGroups = useMemo(
    () =>
      groupByReferrer(
        cases,
        (c) => ({ id: c.referring_doctor_id, name: c.referring_doctor, clinic: c.referring_clinic }),
        'Dentist not recorded',
      ),
    [cases],
  );
  const clinicGroups = useMemo(
    () =>
      groupByReferrer(
        cases,
        (c) => ({ id: c.referring_clinic_id, name: c.referring_clinic }),
        'Clinic not recorded',
      ),
    [cases],
  );
  const [dentistKey, setDentistKey] = useState<string | null>(null);
  // A patient picked inside the dentist popup opens only once the popup has
  // fully closed. Opening both at once leaves Escape and outside clicks
  // aimed at the closing popup, so the patient record would not close.
  const pendingPatient = React.useRef<PatientCaseWithDetails | null>(null);
  const dentistGroup = dentistKey
    ? dentistGroups.find((group) => group.key === dentistKey) ?? null
    : null;
  const openDentist = (record: PatientCaseWithDetails) =>
    setDentistKey(referrerKey(record.referring_doctor_id, record.referring_doctor));

  // -------------------------------------------------------------------------
  // "All patients" tab: search + filter + pagination over EVERY case the
  // doctor can see, closed ones included (Samuel 2026-09-10). The three bands
  // below it stay exactly as they were - this tab is an addition, not a
  // replacement, so nobody loses the triage view they already use.
  // -------------------------------------------------------------------------
  const [search, setSearch] = useState('');
  const [bandFilter, setBandFilter] = useState<StatusBand | 'all'>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  // Period on the date the patient came in (referral received), Samuel 15 Oct 2026.
  const [periodFilter, setPeriodFilter] = useState<PeriodFilter>('all');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 25;

  // Only offer statuses this doctor actually has, so the dropdown never
  // shows a filter that can only return nothing.
  const availableStatuses = useMemo(() => {
    const seen = new Map<string, string>();
    for (const record of cases) {
      const status = canonicalStatus(record.status);
      if (!seen.has(status)) {
        seen.set(status, getStatusMeta(status).label);
      }
    }
    return [...seen.entries()].sort((a, b) => a[1].localeCompare(b[1]));
  }, [cases]);

  const filteredCases = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const { from, to } = periodRange(periodFilter, customFrom, customTo);
    return cases.filter((record) => {
      if (bandFilter !== 'all' && getStatusMeta(record.status).band !== bandFilter) {
        return false;
      }
      if (statusFilter !== 'all' && canonicalStatus(record.status) !== statusFilter) {
        return false;
      }
      if (from || to) {
        const received = record.referral_received_date?.slice(0, 10);
        if (!received) return false;
        if (from && received < from) return false;
        if (to && received > to) return false;
      }
      if (!needle) return true;
      return [
        record.patient_display_name,
        record.plato_patient_number,
        record.plato_reference_id,
        record.referring_doctor,
        record.referring_clinic,
      ]
        .filter(Boolean)
        .some((field) => String(field).toLowerCase().includes(needle));
    });
  }, [cases, search, bandFilter, statusFilter, periodFilter, customFrom, customTo]);

  const totalPages = Math.max(1, Math.ceil(filteredCases.length / PAGE_SIZE));
  // Clamp rather than reset: if a filter shrinks the list while the doctor is
  // on page 6, show the last page instead of an empty table.
  const safePage = Math.min(page, totalPages);
  const pagedCases = useMemo(
    () => filteredCases.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE),
    [filteredCases, safePage],
  );

  const resetToFirstPage = () => setPage(1);

  /**
   * Export exactly what the filters are showing - the whole filtered set, not
   * just the visible page.
   *
   * A doctor can only ever export their own patients, and that is NOT enforced
   * here: `getMyPatientCases(staffId)` already scopes the query, so `cases`
   * never contains anyone else's patient in the first place.
   */
  const handleExport = () => {
    exportRowsToCsv(
      datedFilename('my-patients'),
      filteredCases,
      [
        { header: 'Patient', value: (r) => r.patient_display_name },
        { header: 'ID', value: (r) => r.plato_patient_number ?? '' },
        { header: 'Status', value: (r) => getStatusMeta(r.status).label },
        { header: 'Referred back', value: (r) => (r.referred_back_flag ? 'Yes' : 'No') },
        { header: 'Referred by', value: (r) => r.referring_doctor },
        { header: 'Referring clinic', value: (r) => r.referring_clinic },
        { header: 'Referral received', value: (r) => formatDateSafe(r.referral_received_date) },
        {
          header: 'Last updated',
          value: (r) => formatDateSafe(r.status_changed_at ?? r.updated_at),
        },
      ],
    );
    toast({
      title: 'Export started',
      description: `${filteredCases.length} patient${
        filteredCases.length === 1 ? '' : 's'
      } exported${
        filteredCases.length !== cases.length ? ', matching your current filters' : ''
      }.`,
    });
  };

  // -------------------------------------------------------------------------
  // Guards
  // -------------------------------------------------------------------------

  if (isCheckingDoctor) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isDoctor) {
    return (
      <div className="min-h-screen bg-background">
        <SchedulerHeader
          currentView="patients"
          onViewChange={(view) => navigate(`/${view}`)}
          currentDate={headerDate}
          onNavigate={() => undefined}
          onDateChange={setHeaderDate}
          copiedItemsCount={0}
        />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Doctor access only</AlertTitle>
            <AlertDescription>
              The My Patients view is for internal doctors and specialists. If
              you should have access, ask an admin to enable the doctor flag on
              your staff record.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  if (!staffId) {
    return (
      <div className="min-h-screen bg-background">
        <SchedulerHeader
          currentView="patients"
          onViewChange={(view) => navigate(`/${view}`)}
          currentDate={headerDate}
          onNavigate={() => undefined}
          onDateChange={setHeaderDate}
          copiedItemsCount={0}
        />
        <div className="max-w-3xl mx-auto px-4 py-12">
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>No staff record linked</AlertTitle>
            <AlertDescription>
              Your user account is not linked to a staff profile, so we can't
              filter cases for you. Ask an admin to link your user to your
              staff record.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  const handleRowClick = (record: PatientCaseWithDetails) => {
    setDetailCase(record);
  };

  const handleRefresh = async () => {
    try {
      await refetch();
    } catch (error) {
      toast({
        title: 'Could not refresh cases',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  };

  // -------------------------------------------------------------------------
  // Manual status change (Samuel 2026-09-17): doctors close their own cases
  // without asking an admin - e.g. mark a patient discharged / completed /
  // referred back. Only records already inside `cases` can be opened here, and
  // getMyPatientCases scopes those to this doctor, so the control can never
  // touch another doctor's patient.
  // -------------------------------------------------------------------------
  const openStatusDialog = (record: PatientCaseWithDetails) => {
    setStatusCase(record);
    setStatusValue(canonicalStatus(record.status));
  };

  const handleStatusSave = async () => {
    if (!statusCase || !statusValue || statusValue === canonicalStatus(statusCase.status)) return;
    setIsSavingStatus(true);
    try {
      await updatePatientCase(
        statusCase.id,
        { status: statusValue as PatientCaseStatus },
        user?.id ?? '',
      );
      toast({
        title: 'Status updated',
        description: `${statusCase.patient_display_name} is now ${
          getStatusMeta(statusValue).label
        }.`,
      });
      setStatusCase(null);
      const result = await refetch();
      // Keep an open patient record showing the fresh status.
      const fresh = (result.data ?? []).find(
        (record) => record.id === statusCase.id,
      );
      if (fresh) setDetailCase(fresh);
    } catch (error) {
      toast({
        title: 'Could not update status',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsSavingStatus(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <SchedulerHeader
        currentView="patients"
        onViewChange={(view) => navigate(`/${view}`)}
        currentDate={headerDate}
        onNavigate={() => undefined}
        onDateChange={setHeaderDate}
        copiedItemsCount={0}
      />

      <div className="w-full px-2 py-3 sm:px-4 sm:py-6">
        <div className="max-w-6xl mx-auto space-y-4">
          {/* Header */}
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-3">
            <div>
              <div className="flex items-center gap-2">
                <Stethoscope className="h-5 w-5 text-primary" />
                <h1 className="text-2xl font-semibold text-gray-900">
                  My Patients
                </h1>
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                Signed in as {user?.name || user?.email}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleRefresh}
                disabled={isRefetching}
              >
                {isRefetching ? (
                  <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                ) : (
                  <Activity className="h-4 w-4 mr-1" />
                )}
                Refresh
              </Button>
              {!doctorOnly && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => navigate('/patients')}
                >
                  See all cases
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              )}
            </div>
          </div>

          {/* Info banner */}
          <Alert>
            <Info className="h-4 w-4" />
            <AlertTitle>Your own caseload</AlertTitle>
            <AlertDescription>
              Only cases where you are the assigned owner OR a treating doctor.
              {!doctorOnly && ' To see all cases, use the Patients tab.'}
              {' '}Click a patient to open their record.
            </AlertDescription>
          </Alert>

          {/* Summary cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard
              label="New cases this month"
              value={summary.newThisMonth}
              hint={`Referred to you in ${summary.monthLabel}`}
              icon={<CalendarPlus className="h-4 w-4" />}
              tone="default"
            />
            <SummaryCard
              label="Active"
              value={summary.active}
              icon={<Activity className="h-4 w-4" />}
              tone="active"
            />
            <SummaryCard
              label="Referred back"
              value={summary.referredBack}
              icon={<Undo2 className="h-4 w-4" />}
              tone="referred"
            />
            <SummaryCard
              label="All cases this month"
              value={summary.allThisMonth}
              hint={`Open at any point in ${summary.monthLabel}`}
              icon={<Clock3 className="h-4 w-4" />}
              tone="done"
            />
          </div>

          {/* Referrals received by day (loaded cases only) */}
          {!isLoadingCases && !isError && <DoctorReferralTrend cases={cases} />}

          {/* Error state */}
          {isError && (
            <Alert variant="destructive">
              <AlertTitle>Could not load your cases</AlertTitle>
              <AlertDescription>
                Something went wrong reading patient cases. Try Refresh, or
                contact an admin if it persists.
              </AlertDescription>
            </Alert>
          )}

          {/* Loading state */}
          {isLoadingCases && (
            <Card>
              <CardContent className="py-12 flex items-center justify-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                Loading your cases...
              </CardContent>
            </Card>
          )}

          {/* Empty state */}
          {!isLoadingCases && !isError && cases.length === 0 && (
            <Card>
              <CardContent className="py-12 text-center text-muted-foreground">
                You don't have any patient cases assigned to you yet.
              </CardContent>
            </Card>
          )}

          {/* All patients (table) + the original triage bands, as tabs */}
          {!isLoadingCases && !isError && cases.length > 0 && (
            <Tabs defaultValue="in_progress" className="space-y-4">
              <TabsList className="h-auto max-w-full justify-start overflow-x-auto">
                <TabsTrigger value="in_progress">
                  In progress
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {grouped.in_progress.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="action">
                  Action needed
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {grouped.action.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="all">
                  All patients
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {cases.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="closed">
                  Closed
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {grouped.closed.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="dentists">
                  By referral dentist
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {dentistGroups.length}
                  </Badge>
                </TabsTrigger>
                <TabsTrigger value="clinics">
                  By referral clinic
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {clinicGroups.length}
                  </Badge>
                </TabsTrigger>
              </TabsList>

              <TabsContent value="all" className="mt-0">
                <Card>
                  <CardHeader className="pb-3">
                    <CardTitle className="text-base font-semibold">
                      All my patients
                    </CardTitle>
                    <p className="text-xs text-muted-foreground">
                      Every case assigned to you, including closed ones.
                    </p>
                    <div className="flex flex-col gap-2 pt-2 sm:flex-row sm:items-center">
                      <div className="relative flex-1 min-w-0">
                        <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
                        <Input
                          value={search}
                          onChange={(event) => {
                            setSearch(event.target.value);
                            resetToFirstPage();
                          }}
                          placeholder="Search patient, ID, or referring dentist"
                          className="pl-8"
                          aria-label="Search my patients"
                        />
                      </div>
                      <Select
                        value={bandFilter}
                        onValueChange={(value: StatusBand | 'all') => {
                          setBandFilter(value);
                          resetToFirstPage();
                        }}
                      >
                        <SelectTrigger className="sm:w-[170px]">
                          <SelectValue placeholder="Any group" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any group</SelectItem>
                          <SelectItem value="action">Action needed</SelectItem>
                          <SelectItem value="in_progress">In progress</SelectItem>
                          <SelectItem value="closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select
                        value={statusFilter}
                        onValueChange={(value) => {
                          setStatusFilter(value);
                          resetToFirstPage();
                        }}
                      >
                        <SelectTrigger className="sm:w-[190px]">
                          <SelectValue placeholder="Any status" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="all">Any status</SelectItem>
                          {availableStatuses.map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <Select
                        value={periodFilter}
                        onValueChange={(value: PeriodFilter) => {
                          setPeriodFilter(value);
                          resetToFirstPage();
                        }}
                      >
                        <SelectTrigger className="sm:w-[170px]" aria-label="Period patient came in">
                          <SelectValue placeholder="All period" />
                        </SelectTrigger>
                        <SelectContent>
                          {PERIOD_OPTIONS.map(([value, label]) => (
                            <SelectItem key={value} value={value}>
                              {label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {periodFilter === 'custom' && (
                        <div className="flex items-center gap-1">
                          <Input
                            type="date"
                            value={customFrom}
                            max={customTo || undefined}
                            onChange={(event) => {
                              setCustomFrom(event.target.value);
                              resetToFirstPage();
                            }}
                            aria-label="Came in from"
                            className="sm:w-[150px]"
                          />
                          <span className="text-xs text-muted-foreground">to</span>
                          <Input
                            type="date"
                            value={customTo}
                            min={customFrom || undefined}
                            onChange={(event) => {
                              setCustomTo(event.target.value);
                              resetToFirstPage();
                            }}
                            aria-label="Came in to"
                            className="sm:w-[150px]"
                          />
                        </div>
                      )}
                      <Button
                        variant="outline"
                        onClick={handleExport}
                        disabled={filteredCases.length === 0}
                        title="Download these patients as a CSV, matching the filters above"
                      >
                        <Download className="h-4 w-4 mr-2" />
                        Export
                      </Button>
                    </div>
                  </CardHeader>

                  <CardContent className="px-0 sm:px-6">
                    {filteredCases.length === 0 ? (
                      <div className="py-10 text-center text-sm text-muted-foreground">
                        No patients match that search or filter.
                      </div>
                    ) : (
                      <>
                        <div className="overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>Patient</TableHead>
                                <TableHead className="hidden sm:table-cell">
                                  ID
                                </TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead className="hidden md:table-cell">
                                  Referred by
                                </TableHead>
                                <TableHead className="hidden lg:table-cell">
                                  Last updated
                                </TableHead>
                                <TableHead className="w-28">Update</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {pagedCases.map((record) => {
                                const meta = getStatusMeta(record.status);
                                return (
                                  <TableRow
                                    key={record.id}
                                    onClick={() => handleRowClick(record)}
                                    className="cursor-pointer"
                                  >
                                    <TableCell className="font-medium">
                                      {record.patient_display_name}
                                      {record.referred_back_flag && (
                                        <Badge
                                          variant="outline"
                                          className="ml-2 text-xs font-normal"
                                        >
                                          Referred back
                                        </Badge>
                                      )}
                                    </TableCell>
                                    <TableCell className="hidden sm:table-cell text-muted-foreground">
                                      {record.plato_patient_number || '-'}
                                    </TableCell>
                                    <TableCell>
                                      <Badge
                                        variant="outline"
                                        className={cn(
                                          'text-xs font-normal',
                                          meta.badgeClassName,
                                        )}
                                      >
                                        {meta.label}
                                      </Badge>
                                    </TableCell>
                                    <TableCell className="hidden md:table-cell text-muted-foreground">
                                      {record.referring_doctor ? (
                                        <button
                                          type="button"
                                          className="text-left underline-offset-2 hover:underline hover:text-gray-900"
                                          onClick={(event) => {
                                            event.stopPropagation();
                                            openDentist(record);
                                          }}
                                          title="See your patients from this dentist"
                                        >
                                          {record.referring_doctor}
                                        </button>
                                      ) : (
                                        '—'
                                      )}
                                    </TableCell>
                                    <TableCell className="hidden lg:table-cell text-muted-foreground">
                                      {formatDateSafe(
                                        record.status_changed_at ?? record.updated_at,
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(event) => {
                                          event.stopPropagation();
                                          openStatusDialog(record);
                                        }}
                                        title="Update this patient's status"
                                      >
                                        <Pencil className="h-3.5 w-3.5 mr-1" />
                                        Status
                                      </Button>
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                            </TableBody>
                          </Table>
                        </div>

                        <div className="flex flex-col gap-2 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-0">
                          <p className="text-xs text-muted-foreground">
                            Showing {(safePage - 1) * PAGE_SIZE + 1}–
                            {Math.min(safePage * PAGE_SIZE, filteredCases.length)} of{' '}
                            {filteredCases.length}
                            {filteredCases.length !== cases.length &&
                              ` (filtered from ${cases.length})`}
                          </p>
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => setPage((p) => Math.max(1, p - 1))}
                              disabled={safePage <= 1}
                            >
                              <ChevronLeft className="h-4 w-4" />
                              Previous
                            </Button>
                            <span className="text-xs text-muted-foreground">
                              Page {safePage} of {totalPages}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() =>
                                setPage((p) => Math.min(totalPages, p + 1))
                              }
                              disabled={safePage >= totalPages}
                            >
                              Next
                              <ChevronRight className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </CardContent>
                </Card>
              </TabsContent>

              <TabsContent value="action" className="mt-0">
                <Band
                  title="Action needed"
                  description="Cases waiting on a decision or next step from you."
                  records={grouped.action}
                  onRowClick={handleRowClick}
                  onStatusClick={openStatusDialog}
                  onReferrerClick={openDentist}
                  emptyLabel="Nothing waiting on you right now."
                  accentClass="border-l-amber-400"
                />
              </TabsContent>

              <TabsContent value="in_progress" className="mt-0">
                <Band
                  title="In progress"
                  description="Cases under active treatment or follow-up."
                  records={grouped.in_progress}
                  onRowClick={handleRowClick}
                  onStatusClick={openStatusDialog}
                  onReferrerClick={openDentist}
                  emptyLabel="No cases currently in progress."
                  accentClass="border-l-sky-400"
                />
              </TabsContent>

              <TabsContent value="closed" className="mt-0">
                <Band
                  title="Closed"
                  description="Completed, referred back, or inactive cases."
                  records={grouped.closed}
                  onRowClick={handleRowClick}
                  onStatusClick={openStatusDialog}
                  onReferrerClick={openDentist}
                  emptyLabel="No closed cases."
                  accentClass="border-l-slate-300"
                  collapsible
                  expanded={closedExpanded}
                  onToggle={() => setClosedExpanded((v) => !v)}
                />
              </TabsContent>

              <TabsContent value="dentists" className="mt-0">
                <ReferrerGroups
                  title="By referral dentist"
                  description="Your patients grouped by the dentist who referred them."
                  icon={<Users className="h-4 w-4" />}
                  groups={dentistGroups}
                  onRowClick={handleRowClick}
                  onStatusClick={openStatusDialog}
                />
              </TabsContent>

              <TabsContent value="clinics" className="mt-0">
                <ReferrerGroups
                  title="By referral clinic"
                  description="Your patients grouped by the clinic that referred them."
                  icon={<Building2 className="h-4 w-4" />}
                  groups={clinicGroups}
                  onRowClick={handleRowClick}
                  onStatusClick={openStatusDialog}
                  onReferrerClick={openDentist}
                />
              </TabsContent>
            </Tabs>
          )}

          {/* Referring dentist (Samuel 8 Oct 2026): clicking "Referred by"
              shows this doctor's own patients from that dentist. It never
              loads the dentist's wider list or contact details. */}
          <Dialog
            open={dentistGroup !== null}
            onOpenChange={(open) => {
              if (!open) setDentistKey(null);
            }}
          >
            <DialogContent
              className="sm:max-w-lg"
              onCloseAutoFocus={(event) => {
                const picked = pendingPatient.current;
                if (!picked) return;
                pendingPatient.current = null;
                event.preventDefault();
                setDetailCase(picked);
              }}
            >
              <DialogHeader>
                <DialogTitle>{dentistGroup?.label}</DialogTitle>
                <DialogDescription>
                  {dentistGroup
                    ? [
                        dentistGroup.clinics.join(', '),
                        dentistGroup.otherLabels.length
                          ? `Also recorded as ${dentistGroup.otherLabels.join(', ')}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ') || 'Referring dentist'
                    : ''}
                </DialogDescription>
              </DialogHeader>
              {dentistGroup && (
                <div className="space-y-3">
                  <BandCounts items={dentistGroup.items} />
                  <div className="max-h-[50vh] overflow-y-auto divide-y divide-gray-100">
                    {dentistGroup.items.map((record) => (
                      <CaseRow
                        key={record.id}
                        record={record}
                        onClick={(picked) => {
                          pendingPatient.current = picked;
                          setDentistKey(null);
                        }}
                        onStatusClick={openStatusDialog}
                      />
                    ))}
                  </div>
                </div>
              )}
            </DialogContent>
          </Dialog>

          {/* Patient record (Samuel 2026-09-17): clicking a patient opens
              their record here. Doctor-only logins cannot use /patients/:id
              (Index bounces them back), so the detail lives on this page. */}
          <Dialog
            open={detailCase !== null}
            onOpenChange={(open) => {
              if (!open) setDetailCase(null);
            }}
          >
            <DialogContent className="sm:max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2 flex-wrap">
                  {detailCase?.patient_display_name}
                  {detailCase && (
                    <Badge
                      variant="outline"
                      className={cn(
                        'text-xs font-normal',
                        getStatusMeta(detailCase.status).badgeClassName,
                      )}
                    >
                      {getStatusMeta(detailCase.status).label}
                    </Badge>
                  )}
                  {detailCase?.referred_back_flag && (
                    <Badge
                      variant="outline"
                      className="text-xs font-normal border-violet-300 bg-violet-50 text-violet-900"
                    >
                      Referred back
                    </Badge>
                  )}
                </DialogTitle>
                <DialogDescription>
                  {detailCase
                    ? [
                        detailCase.plato_patient_number
                          ? `ID ${detailCase.plato_patient_number}`
                          : null,
                        detailCase.referring_doctor
                          ? `Referred by ${detailCase.referring_doctor}`
                          : null,
                        detailCase.referring_clinic,
                      ]
                        .filter(Boolean)
                        .join(' · ')
                    : ''}
                </DialogDescription>
              </DialogHeader>

              {detailCase && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                    <div>
                      <p className="text-xs text-muted-foreground">Referral received</p>
                      <p>{formatDateSafe(detailCase.referral_received_date) ?? '—'}</p>
                    </div>
                    <div>
                      <p className="text-xs text-muted-foreground">Last updated</p>
                      <p>
                        {formatDateSafe(
                          detailCase.status_changed_at ?? detailCase.updated_at,
                          'd MMM yyyy, h:mm a',
                        ) ?? '—'}
                      </p>
                    </div>
                    {detailCase.next_action && (
                      <div className="col-span-2">
                        <p className="text-xs text-muted-foreground">Next</p>
                        <p>
                          {detailCase.next_action}
                          {formatDateSafe(detailCase.next_action_due_date)
                            ? ` · due ${formatDateSafe(detailCase.next_action_due_date)}`
                            : ''}
                        </p>
                      </div>
                    )}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => openStatusDialog(detailCase)}
                  >
                    <Pencil className="h-3.5 w-3.5 mr-1" />
                    Update status
                  </Button>

                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Case history
                    </p>
                    {isLoadingActivities ? (
                      <p className="text-xs text-muted-foreground flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Loading history...
                      </p>
                    ) : detailActivities.length === 0 ? (
                      <p className="text-xs text-muted-foreground">
                        No recorded changes yet.
                      </p>
                    ) : (
                      <div className="max-h-44 overflow-y-auto space-y-1.5 pr-1">
                        {detailActivities.slice(0, 30).map((activity) => (
                          <div key={activity.id} className="text-xs">
                            <span className="font-medium text-gray-900">
                              {activitySummary(activity)}
                            </span>
                            <span className="text-muted-foreground">
                              {' '}· {ACTIVITY_LABELS[activity.activity_type] ?? activity.activity_type}
                              {' '}· {formatDateSafe(activity.created_at, 'd MMM yyyy, h:mm a')}
                              {activity.created_by_user?.name
                                ? ` · by ${activity.created_by_user.name}`
                                : ''}
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div>
                    <p className="text-xs font-medium text-muted-foreground mb-1">
                      Clinic visits
                    </p>
                    <PatientVisitHistory
                      platoReferenceId={detailCase.plato_reference_id ?? null}
                    />
                  </div>
                </div>
              )}

              <DialogFooter>
                <Button variant="outline" onClick={() => setDetailCase(null)}>
                  Close
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Manual status change (Samuel 2026-09-17) — doctors can close or
              progress their own cases, e.g. mark a patient discharged. */}
          <Dialog
            open={statusCase !== null}
            onOpenChange={(open) => {
              if (!open) setStatusCase(null);
            }}
          >
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Update patient status</DialogTitle>
                <DialogDescription>
                  {statusCase
                    ? `${statusCase.patient_display_name} — currently ${
                        getStatusMeta(statusCase.status).label
                      }. Staff see the same change in the referral list.`
                    : ''}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3">
                <Select value={statusValue} onValueChange={setStatusValue}>
                  <SelectTrigger aria-label="New status">
                    <SelectValue placeholder="Choose a status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      <SelectLabel>Treatment progression</SelectLabel>
                      {DOCTOR_MAIN_LADDER.map((status) => (
                        <SelectItem key={status} value={status}>
                          {getStatusMeta(status).label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                    <SelectGroup>
                      <SelectLabel>Other outcomes</SelectLabel>
                      {DOCTOR_OFF_RAMPS.map((status) => (
                        <SelectItem key={status} value={status}>
                          {getStatusMeta(status).label}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
                <p className="text-xs text-muted-foreground">
                  The change is recorded on the case history with your name.
                </p>
              </div>
              <DialogFooter>
                <Button
                  variant="outline"
                  onClick={() => setStatusCase(null)}
                  disabled={isSavingStatus}
                >
                  Cancel
                </Button>
                <Button
                  onClick={handleStatusSave}
                  disabled={
                    !statusCase ||
                    !statusValue ||
                    statusValue === canonicalStatus(statusCase.status) ||
                    isSavingStatus
                  }
                >
                  {isSavingStatus && (
                    <Loader2 className="h-4 w-4 mr-1 animate-spin" />
                  )}
                  Update status
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// SummaryCard
// ---------------------------------------------------------------------------

interface SummaryCardProps {
  label: string;
  value: number;
  hint?: string;
  icon: React.ReactNode;
  tone: 'default' | 'active' | 'referred' | 'done';
}

const TONE_CLASS: Record<SummaryCardProps['tone'], string> = {
  default: 'bg-white',
  active: 'bg-sky-50 border-sky-200',
  referred: 'bg-violet-50 border-violet-200',
  done: 'bg-emerald-50 border-emerald-200',
};

const SummaryCard: React.FC<SummaryCardProps> = ({ label, value, hint, icon, tone }) => (
  <Card className={cn('shadow-sm', TONE_CLASS[tone])}>
    <CardContent className="p-4">
      <div className="flex items-center justify-between text-sm text-muted-foreground">
        <span>{label}</span>
        {icon}
      </div>
      <div className="text-2xl font-semibold text-gray-900 mt-1">{value}</div>
      {hint && <p className="text-xs text-muted-foreground mt-0.5">{hint}</p>}
    </CardContent>
  </Card>
);

// ---------------------------------------------------------------------------
// Band
// ---------------------------------------------------------------------------

interface BandProps {
  title: string;
  description: string;
  records: PatientCaseWithDetails[];
  onRowClick: (record: PatientCaseWithDetails) => void;
  onStatusClick: (record: PatientCaseWithDetails) => void;
  onReferrerClick?: (record: PatientCaseWithDetails) => void;
  emptyLabel: string;
  accentClass: string;
  collapsible?: boolean;
  expanded?: boolean;
  onToggle?: () => void;
}

const Band: React.FC<BandProps> = ({
  title,
  description,
  records,
  onRowClick,
  onStatusClick,
  onReferrerClick,
  emptyLabel,
  accentClass,
  collapsible = false,
  expanded = true,
  onToggle,
}) => {
  const showRows = !collapsible || expanded;

  return (
    <Card className={cn('border-l-4', accentClass)}>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-base font-semibold flex items-center gap-2">
              {title}
              <Badge variant="secondary" className="text-xs">
                {records.length}
              </Badge>
            </CardTitle>
            <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
          </div>
          {collapsible && (
            <Button
              variant="ghost"
              size="sm"
              onClick={onToggle}
              className="text-xs"
            >
              {expanded ? 'Hide' : 'Show'}
              <ChevronDown
                className={cn(
                  'h-4 w-4 ml-1 transition-transform',
                  expanded && 'rotate-180'
                )}
              />
            </Button>
          )}
        </div>
      </CardHeader>
      {showRows && (
        <CardContent className="pt-0 pb-2">
          {records.length === 0 ? (
            <div className="text-sm text-muted-foreground py-4 px-1">
              {emptyLabel}
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {records.map((record) => (
                <CaseRow
                  key={record.id}
                  record={record}
                  onClick={onRowClick}
                  onStatusClick={onStatusClick}
                  onReferrerClick={onReferrerClick}
                />
              ))}
            </div>
          )}
        </CardContent>
      )}
    </Card>
  );
};

// ---------------------------------------------------------------------------
// Referral groups (by dentist / by clinic)
// ---------------------------------------------------------------------------

const BandCounts: React.FC<{ items: PatientCaseWithDetails[] }> = ({ items }) => {
  const count = (band: StatusBand) =>
    items.filter((record) => getStatusMeta(record.status).band === band).length;
  return (
    <div className="flex flex-wrap gap-1.5 text-xs">
      <Badge variant="outline" className="font-normal">{items.length} total</Badge>
      <Badge variant="outline" className="font-normal border-sky-300 bg-sky-50 text-sky-900">
        {count('in_progress')} in progress
      </Badge>
      <Badge variant="outline" className="font-normal border-amber-300 bg-amber-50 text-amber-900">
        {count('action')} action needed
      </Badge>
      <Badge variant="outline" className="font-normal border-slate-300 bg-slate-100 text-slate-700">
        {count('closed')} closed
      </Badge>
    </div>
  );
};

interface ReferrerGroupsProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  groups: ReferrerGroup<PatientCaseWithDetails>[];
  onRowClick: (record: PatientCaseWithDetails) => void;
  onStatusClick: (record: PatientCaseWithDetails) => void;
  onReferrerClick?: (record: PatientCaseWithDetails) => void;
}

const ReferrerGroups: React.FC<ReferrerGroupsProps> = ({
  title,
  description,
  icon,
  groups,
  onRowClick,
  onStatusClick,
  onReferrerClick,
}) => {
  const [openKey, setOpenKey] = useState<string | null>(null);
  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          {icon}
          {title}
          <Badge variant="secondary" className="text-xs">
            {groups.length}
          </Badge>
        </CardTitle>
        <p className="text-xs text-muted-foreground mt-0.5">{description}</p>
      </CardHeader>
      <CardContent className="pt-0 pb-2 divide-y divide-gray-100">
        {groups.map((group) => {
          const open = openKey === group.key;
          return (
            <div key={group.key}>
              <button
                type="button"
                onClick={() => setOpenKey(open ? null : group.key)}
                aria-expanded={open}
                className="w-full text-left py-3 px-1 hover:bg-gray-50 rounded flex items-start gap-3"
              >
                <div className="flex-1 min-w-0 space-y-1.5">
                  <div className="font-medium text-gray-900">{group.label}</div>
                  {(group.clinics.length > 0 || group.otherLabels.length > 0) && (
                    <div className="text-xs text-muted-foreground">
                      {[
                        group.clinics.join(', '),
                        group.otherLabels.length
                          ? `Also recorded as ${group.otherLabels.join(', ')}`
                          : null,
                      ]
                        .filter(Boolean)
                        .join(' · ')}
                    </div>
                  )}
                  <BandCounts items={group.items} />
                </div>
                <ChevronDown
                  className={cn(
                    'h-4 w-4 mt-1 shrink-0 text-gray-400 transition-transform',
                    open && 'rotate-180',
                  )}
                />
              </button>
              {open && (
                <div className="pl-3 pb-2 divide-y divide-gray-100 border-l-2 border-gray-100 ml-1">
                  {group.items.map((record) => (
                    <CaseRow
                      key={record.id}
                      record={record}
                      onClick={onRowClick}
                      onStatusClick={onStatusClick}
                      onReferrerClick={onReferrerClick}
                    />
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

// ---------------------------------------------------------------------------
// CaseRow
// ---------------------------------------------------------------------------

interface CaseRowProps {
  record: PatientCaseWithDetails;
  onClick: (record: PatientCaseWithDetails) => void;
  onStatusClick: (record: PatientCaseWithDetails) => void;
  /** When set, "Referred by" is a link that opens the dentist popup. */
  onReferrerClick?: (record: PatientCaseWithDetails) => void;
}

const CaseRow: React.FC<CaseRowProps> = ({ record, onClick, onStatusClick, onReferrerClick }) => {
  const meta = getStatusMeta(record.status);
  const nextActionDue = formatDateSafe(record.next_action_due_date);
  const lastUpdated = formatDateSafe(record.status_changed_at ?? record.updated_at);
  // Per-case collapsible Plato visit history — the row click opens the
  // patient's record in a dialog; this is the quick inline peek.
  const [visitsOpen, setVisitsOpen] = useState(false);

  return (
    <div>
      <div className="flex items-start gap-1">
        <div className="flex-1 min-w-0">
          <button
            type="button"
            onClick={() => onClick(record)}
            className="w-full text-left py-3 px-1 hover:bg-gray-50 transition-colors flex items-start gap-3 rounded"
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium text-gray-900 truncate">
                  {record.patient_display_name}
                </span>
                {record.plato_patient_number && (
                  <span className="text-xs text-muted-foreground">
                    ID {record.plato_patient_number}
                  </span>
                )}
                <Badge
                  variant="outline"
                  className={cn('text-xs font-normal', meta.badgeClassName)}
                >
                  {meta.label}
                </Badge>
                {record.referred_back_flag && (
                  <Badge
                    variant="outline"
                    className="text-xs font-normal border-violet-300 bg-violet-50 text-violet-900"
                  >
                    Referred back
                  </Badge>
                )}
              </div>
              <div className="mt-1 text-xs text-muted-foreground flex flex-wrap items-center gap-x-3 gap-y-1">
                {record.referring_doctor && !onReferrerClick && (
                  <span>Referred by {record.referring_doctor}</span>
                )}
                {record.referring_clinic && <span>{record.referring_clinic}</span>}
                {record.next_action && (
                  <span className="text-gray-700">Next: {record.next_action}</span>
                )}
                {nextActionDue && <span>Due {nextActionDue}</span>}
                {lastUpdated && <span>Updated {lastUpdated}</span>}
              </div>
            </div>
            <ChevronRight className="h-4 w-4 text-gray-300 mt-1 shrink-0" />
          </button>
          {record.referring_doctor && onReferrerClick && (
            <button
              type="button"
              onClick={() => onReferrerClick(record)}
              className="-mt-2 mb-2 px-1 text-xs text-muted-foreground underline-offset-2 hover:underline hover:text-gray-900"
              title="See your patients from this dentist"
            >
              Referred by {record.referring_doctor}
            </button>
          )}
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onStatusClick(record)}
          className="mt-2 shrink-0 text-xs text-muted-foreground"
          title="Update this patient's status"
        >
          <Pencil className="h-4 w-4 mr-1" />
          Status
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setVisitsOpen((open) => !open)}
          className="mt-2 shrink-0 text-xs text-muted-foreground"
          title="Plato visit history"
        >
          <History className="h-4 w-4 mr-1" />
          Visits
          <ChevronDown
            className={cn(
              'h-3.5 w-3.5 ml-1 transition-transform',
              visitsOpen && 'rotate-180'
            )}
          />
        </Button>
      </div>
      {visitsOpen && (
        <div className="px-1 pb-3">
          <PatientVisitHistory platoReferenceId={record.plato_reference_id ?? null} />
        </div>
      )}
    </div>
  );
};

export default MyPatients;
